import React, { useState, Component } from 'react';
import { AdminSidebar } from './AdminSidebar';
import { DashboardPage } from './pages/DashboardPage';
import { UsersPage } from './pages/UsersPage';
import { QuestionBankPage } from './pages/QuestionBankPage';
import { GovernancePage } from './pages/GovernancePage';
import { SupportContentPage } from './pages/SupportContentPage';
import { DiagnosticsPage } from './pages/DiagnosticsPage';
import { LogsPage } from './pages/LogsPage';
import { SettingsPage } from './pages/SettingsPage';
import { MascotPage } from './pages/MascotPage';
import { LdgGraphPage } from './pages/LdgGraphPage';
import { SessionAnalyticsPage } from './pages/SessionAnalyticsPage';
import { type AccountsState, type Account } from '../../accounts';
import { ModuleId, Question } from '../../content';
import { AppState } from '../../app/types';
import { Menu, X, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { BaseErrorBoundary } from '../../components/shared/BaseErrorBoundary';
const ADMIN_PASSWORD = 'djaymeester2026';
const ADMIN_UNLOCK_KEY = 'admin_unlocked';
export type AdminRoute = 'dashboard' | 'users' | 'question-bank' | 'governance' | 'support-content' | 'ldg-graph' | 'mascot' | 'diagnostics' | 'logs' | 'settings' | 'analytics';
/** Props passed to all admin page components via spread */
type AdminPageProps = AdminShellProps & {
  onNavigate: (route: string) => void;
};
const PAGE_MAP: Record<AdminRoute, ComponentType<Partial<AdminPageProps>>> = {
  dashboard: DashboardPage,
  users: UsersPage,
  'question-bank': QuestionBankPage,
  governance: GovernancePage,
  'support-content': SupportContentPage,
  'ldg-graph': LdgGraphPage,
  mascot: MascotPage,
  diagnostics: DiagnosticsPage,
  logs: LogsPage,
  settings: SettingsPage,
  analytics: SessionAnalyticsPage
};
const ROUTE_LABELS: Record<AdminRoute, string> = {
  dashboard: 'Dashboard',
  users: 'Gebruikers',
  'question-bank': 'Vragenbank',
  governance: 'Governance & Kwaliteit',
  'support-content': 'Ondersteuning',
  'ldg-graph': 'Leerpad Graaf',
  mascot: 'Mascotte Djay',
  diagnostics: 'Diagnostiek',
  logs: 'Activiteitenlog',
  settings: 'Instellingen',
  analytics: 'Sessie Analytics'
};
interface AdminShellProps {
  accounts?: AccountsState;
  getLearnerData?: (learnerId: string) => AppState | null;
  getLearnerModuleMastery?: (learnerId: string, moduleId: ModuleId) => number;
  selectedGroup?: number;
  onNavigate?: (view: string) => void;
  onNavigateToLearner?: (learnerId: string) => void;
  allAccounts?: Account[];
  activeAccountId?: string;
  onSwitchAccount?: (accountId: string) => void;
  onLogout?: () => void;
  questions?: Question[];
  state?: AppState;
  onReturnHome?: () => void;
}
export function AdminShell(props: AdminShellProps) {
  const [activeRoute, setActiveRoute] = useState<AdminRoute>('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(() => sessionStorage.getItem(ADMIN_UNLOCK_KEY) === 'true');
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordInput === ADMIN_PASSWORD) {
      sessionStorage.setItem(ADMIN_UNLOCK_KEY, 'true');
      setIsUnlocked(true);
      setPasswordError(false);
    } else {
      setPasswordError(true);
      setPasswordInput('');
    }
  };
  if (!isUnlocked) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-3779">
        <div className="w-full max-w-sm" data-id="element-3780">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8" data-id="element-3781">
            <div className="flex flex-col items-center mb-6" data-id="element-3782">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-xl flex items-center justify-center shadow-md mb-4" data-id="element-3783">
                <Lock className="w-6 h-6 text-white" data-id="element-3784" />
              </div>
              <h1 className="text-lg font-bold text-gray-900" data-id="element-3785">Admin Portaal</h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-3786">
                Voer het wachtwoord in om verder te gaan
              </p>
            </div>
            <form onSubmit={handlePasswordSubmit} className="space-y-4" data-id="element-3787">
              <div data-id="element-3788">
                <div className="relative" data-id="element-3789">
                  <input type={showPassword ? 'text' : 'password'} value={passwordInput} onChange={e => {
                  setPasswordInput(e.target.value);
                  setPasswordError(false);
                }} placeholder="Wachtwoord" autoFocus className={`w-full px-4 py-3 pr-11 rounded-xl border-2 text-sm transition-colors focus:outline-none focus:ring-0 ${passwordError ? 'border-red-300 bg-red-50 focus:border-red-400' : 'border-gray-200 bg-gray-50 focus:border-indigo-400 focus:bg-white'}`} data-id="element-3790" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors" tabIndex={-1} data-id="element-3791">
                    {showPassword ? <EyeOff className="w-4 h-4" data-id="element-3792" /> : <Eye className="w-4 h-4" data-id="element-3793" />}
                  </button>
                </div>
                {passwordError && <p className="text-xs text-red-500 mt-2 pl-1" data-id="element-3794">
                    Onjuist wachtwoord. Probeer het opnieuw.
                  </p>}
              </div>
              <button type="submit" disabled={!passwordInput.trim()} className="w-full py-3 bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-sm font-semibold rounded-xl hover:from-indigo-700 hover:to-violet-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm" data-id="element-3795">
                Inloggen
              </button>
            </form>
          </div>
          <p className="text-xs text-gray-400 text-center mt-4" data-id="element-3796">
            Alleen voor beheerders
          </p>
        </div>
      </div>;
  }
  const PageComponent = PAGE_MAP[activeRoute] ?? DashboardPage;
  return <div className="flex h-screen bg-gray-50 overflow-hidden" data-id="element-3797">
      {/* Skip to content — a11y (A11Y-01 fix) */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[100] focus:px-4 focus:py-2 focus:bg-indigo-600 focus:text-white focus:rounded-lg focus:text-sm focus:font-bold" data-id="element-3798">
        Ga naar inhoud
      </a>

      {/* Mobile Header with safe area */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-gray-200 z-30 flex items-center justify-between px-4 shadow-sm" style={{
      paddingTop: 'max(0.875rem, env(safe-area-inset-top))'
    }} data-id="element-3799">
        <div className="font-bold text-gray-900 flex items-center gap-2.5 py-3" data-id="element-3800">
          <div className="w-7 h-7 bg-gradient-to-br from-indigo-600 to-violet-600 rounded-lg flex items-center justify-center shadow-sm" data-id="element-3801">
            <span className="text-white font-black text-sm" data-id="element-3802">M</span>
          </div>
          <span className="text-sm" data-id="element-3803">{ROUTE_LABELS[activeRoute]}</span>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2.5 -mr-1 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500" aria-label={mobileMenuOpen ? 'Menu sluiten' : 'Menu openen'} data-id="element-3804">
          {mobileMenuOpen ? <X className="w-5 h-5" data-id="element-3805" /> : <Menu className="w-5 h-5" data-id="element-3806" />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className={`md:hidden fixed inset-0 bg-gray-900/40 z-40 transition-opacity duration-300 ${mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} onClick={() => setMobileMenuOpen(false)} data-id="element-3807" />

      {/* Sidebar - Desktop & Mobile Drawer */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 md:z-0 will-change-transform
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `} style={{
      paddingBottom: 'env(safe-area-inset-bottom)'
    }} data-id="element-3808">
        <AdminSidebar activeRoute={activeRoute} onNavigate={route => {
        setActiveRoute(route);
        setMobileMenuOpen(false);
      }} onLogout={props.onLogout} onReturnHome={props.onReturnHome} data-id="element-3809" />
      </div>

      {/* Main Content */}
      <main id="main-content" className="flex-1 min-w-0 min-h-0 overflow-hidden pt-14 md:pt-0" data-id="element-3810">
        <BaseErrorBoundary logPrefix="Admin Portal" resetKey={activeRoute} fallback={({
        error,
        onReset
      }) => <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center" data-id="element-3812">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4" data-id="element-3813">
                <AlertCircle className="w-8 h-8 text-indigo-600" data-id="element-3814" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-2" data-id="element-3815">
                Er ging iets mis
              </h2>
              <p className="text-sm text-gray-500 mb-4 max-w-md" data-id="element-3816">
                {error.message}
              </p>
              <button onClick={onReset} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-3817">
                Probeer opnieuw
              </button>
            </div>} data-id="element-3811">
          <PageComponent {...props} onNavigate={(route: string) => setActiveRoute(route as AdminRoute)} data-id="element-3818" />
        </BaseErrorBoundary>
      </main>
    </div>;
}